import { isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useQueryClient } from 'react-query';
import { VyLayout } from '../../../components/new-design/layout/VyLayout';
import { withGlobalSearchParams } from '../../../components/utility/GlobalSearchParamsBoundary';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import { useContractPage } from '../../../hooks/requests/pages/use-contract-page';
import { prefetchContractBillingSchedule } from '../../../hooks/requests/use-contract-billing-schedule';
import { useValidatedParams } from '../../../hooks/use-validated-params';
import { hasNetSuiteIntegration } from '../../../utils/integration-utils';
import { ContractContent } from '../contract-page-components/ContractContent/ContractContent';
import { ContractContentLoader } from '../contract-page-components/ContractContent/ContractContentLoader';
import { ContractPageFallbackComponent } from '../contract-page-components/ContractPageFallbackComponent';

const ContractPage: React.FC = () => {
  const { contractId } = useValidatedParams<['contractId']>('contractId');
  const queryClient = useQueryClient();

  // Overlap with the page-data fetch; the billing-schedule suspense hook below would otherwise waterfall.
  void prefetchContractBillingSchedule(contractId, queryClient);

  const { data, isLoading } = useContractPage(contractId, { suspense: true });
  const contract = useMemo(() => data?.contractPageData?.contract, [data]);

  const isNetSuiteSynced = hasNetSuiteIntegration(contract?.integrationEntities);
  const isContractHasPhases = (data?.contractPageData?.versions.length ?? 0) > 1;

  if (isNullOrUndefined(contract) && !isLoading) {
    return <ContractPageFallbackComponent text="Contract Not Found" />;
  }

  return (
    <>
      <Helmet>
        <title>{`Contract ${contract?.plan.name ?? ''}`}</title>
      </Helmet>

      <VyLayout>
        {isNotNullOrUndefined(data) && isNotNullOrUndefined(contract) ? (
          <ContractContent
            contractPageData={data.contractPageData}
            customFieldsWithDefinitions={data.customFieldsWithDefinitions}
            customFieldDefinitions={data.customFieldDefinitions}
            isEditable={contract?.isEditable}
            isNetSuiteSynced={isNetSuiteSynced}
            isContractHasPhases={isContractHasPhases}
          />
        ) : (
          <ContractContentLoader />
        )}
      </VyLayout>
    </>
  );
};

const ContractPageWithSuspenseBoundary = withSuspenseBoundary(withGlobalSearchParams(ContractPage));
export { ContractPageWithSuspenseBoundary as ContractPage };
